import React, { useState } from "react";
import axios from "axios";
import { useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import logo from "@/assets/bubble-radar-logo.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = logo;
const LOGIN_BOOTSTRAP_KEY = "bubble-radar-login-bootstrap";

export default function LoginPage() {
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const attemptLogin = async () => {
    const passwordVariants = [password];
    const trimmedPassword = password.trim();
    if (trimmedPassword && trimmedPassword !== password) {
      passwordVariants.push(trimmedPassword);
    }

    let lastError = null;
    for (const candidatePassword of passwordVariants) {
      for (let attempt = 1; attempt <= 2; attempt += 1) {
        try {
          return await axios.post(
            `${API}/auth/login`,
            { email: email.trim(), password: candidatePassword },
            { withCredentials: true },
          );
        } catch (err) {
          lastError = err;
          const isUnauthorized = err?.response?.status === 401;
          const isTransient = !err?.response;
          if (attempt < 2 && (isUnauthorized || isTransient)) {
            await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
            continue;
          }
          break;
        }
      }
    }

    throw lastError;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const next = new URLSearchParams(loc.search).get("next");
      const loginResponse = await attemptLogin();

      let role = loginResponse?.data?.role;
      let hasAccess = loginResponse?.data?.hasAccess;

      if (!role || typeof hasAccess !== "boolean") {
        try {
          const me = await axios.get(`${API}/me`, { withCredentials: true });
          role = me?.data?.role;
          hasAccess = Boolean(me?.data?.hasAccess);
        } catch {
          // Keep fallback from login response when /me is unavailable momentarily.
        }
      }

      let target = "/app";
      if (role === "admin") {
        target = next || "/admin/users";
      } else if (hasAccess === false) {
        target = "/billing";
      } else if (next) {
        target = next;
      }

      window.sessionStorage.setItem(LOGIN_BOOTSTRAP_KEY, JSON.stringify({
        role,
        hasAccess,
        createdAt: Date.now(),
      }));

      window.location.replace(target);
      return;
    } catch (err) {
      window.sessionStorage.removeItem(LOGIN_BOOTSTRAP_KEY);
      toast.error(err?.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md -mt-6">
        <div className="flex justify-center mb-10" data-testid="login-logo-wrapper">
          <img
            src={LOGO_URL}
            alt="Bubble Radar logo"
            data-testid="login-logo"
            className="h-40 w-auto"
          />
        </div>
        <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-lg">Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm text-slate-600">Email</label>
              <Input
                id="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm text-slate-600">Password</label>
              <Input
                id="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
            <div className="text-sm text-slate-600 flex flex-col gap-1 items-center text-center">
              <Link to="/forgot-password" className="hover:text-slate-900" data-testid="login-forgot-password-link">
                Forgot password?
              </Link>
              <Link to="/signup" className="hover:text-slate-900" data-testid="login-create-account-link">
                Create account
              </Link>
              <Link
                to="/resend-verification"
                className="hover:text-slate-900"
                data-testid="login-resend-verification-link"
              >
                Resend verification email
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  </div>
  );
}
